import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import {
  Bell,
  BookOpen,
  Boxes,
  ChevronRight,
  Clock3,
  FileDown,
  Inbox,
  Lightbulb,
  Menu,
  MessageCircleQuestion,
  Plus,
  Pin,
  Search,
  Settings,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { zipSync, strToU8 } from "fflate";
import { api } from "./api";
import type { MemoryCard, Reminder } from "./types";
import { CARD_LABELS } from "./types";
import LibraryWorkspace from "./components/LibraryWorkspace";
import { popularTags } from "./library-model";

const KnowledgeMap = lazy(() => import("./components/KnowledgeMap"));

const navItems = [
  { to: "/today", label: "Сегодня", icon: Inbox },
  { to: "/library", label: "Библиотека", icon: BookOpen },
  { to: "/ask", label: "Поиск и вопросы", icon: MessageCircleQuestion },
  { to: "/reminders", label: "Напоминания", icon: Bell },
  { to: "/map", label: "Карта знаний", icon: Boxes },
  { to: "/trash", label: "Корзина", icon: Trash2 },
  { to: "/settings", label: "Настройки", icon: Settings },
];

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short", year: "numeric" }).format(new Date(value));
}

function ErrorNotice({ error }: { error: string | null }) {
  return error ? <div className="notice notice-error">Не удалось загрузить данные: {error}</div> : null;
}

function SidebarLibrarySections({ onNavigate }: { onNavigate: () => void }) {
  const location = useLocation();
  const inLibrary = location.pathname.startsWith("/library");
  const [cards, setCards] = useState<MemoryCard[]>([]);
  useEffect(() => {
    if (!inLibrary) return;
    api.listCards({ archived: "false", limit: "100" }).then((result) => setCards(result.cards)).catch(() => undefined);
  }, [inLibrary]);
  if (!inLibrary) return null;
  const params = new URLSearchParams(location.search);
  const currentType = params.get("type") ?? "";
  const currentTag = params.get("tag") ?? "";
  const pinned = params.get("pinned") === "true";
  const archived = params.get("archived") === "true";
  const tags = popularTags(cards, 10);
  return (
    <div className="sidebar-library">
      <span className="sidebar-section-title">Разделы</span>
      <Link className={!currentType && !currentTag && !pinned && !archived ? "sidebar-filter active" : "sidebar-filter"} to="/library" onClick={onNavigate}><span>Все записи</span><small>{cards.length}</small></Link>
      {Object.entries(CARD_LABELS).map(([type, label]) => (
        <Link key={type} className={currentType === type ? "sidebar-filter active" : "sidebar-filter"} to={"/library?type=" + type} onClick={onNavigate}>
          <span><i className={"type-dot type-" + type} />{label}</span>
          <small>{cards.filter((card) => card.type === type).length}</small>
        </Link>
      ))}
      <Link className={pinned ? "sidebar-filter active" : "sidebar-filter"} to="/library?pinned=true" onClick={onNavigate}><span><Pin size={14} />Закреплённые</span><small>{cards.filter((card) => card.metadata.pinned === true).length}</small></Link>
      <Link className={archived ? "sidebar-filter active" : "sidebar-filter"} to="/library?archived=true" onClick={onNavigate}><span>Архив</span></Link>
      {tags.length > 0 && <><span className="sidebar-section-title tag-title">Теги</span><div className="sidebar-tags">{tags.map((tag) => <Link key={tag.name} className={currentTag === tag.name ? "active" : ""} to={"/library?tag=" + encodeURIComponent(tag.name)} onClick={onNavigate}><span>#{tag.name}</span><small>{tag.count}</small></Link>)}</div></>}
    </div>
  );
}

function Shell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const inLibrary = location.pathname.startsWith("/library");
  return (
    <div className="shell">
      <aside className={menuOpen ? "sidebar sidebar-open" : "sidebar"}>
        <div className="brand">
          <span className="brand-mark"><Lightbulb size={20} /></span>
          <div><strong>Idea-bot</strong><small>личный архив</small></div>
          <button className="icon-button close-menu" onClick={() => setMenuOpen(false)} aria-label="Закрыть меню"><X /></button>
        </div>
        <nav aria-label="Основная навигация">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} onClick={() => setMenuOpen(false)} className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>
              <Icon size={18} /><span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <SidebarLibrarySections onNavigate={() => setMenuOpen(false)} />
        {!inLibrary && <div className="sidebar-note"><Sparkles size={17} /><span>Всё важное — в одном тихом месте.</span></div>}
      </aside>
      {menuOpen && <button className="menu-scrim" onClick={() => setMenuOpen(false)} aria-label="Закрыть меню" />}
      <main className={inLibrary ? "main main-library" : "main"}>
        <button className="icon-button mobile-menu" onClick={() => setMenuOpen(true)} aria-label="Открыть меню"><Menu /></button>
        <Routes>
          <Route path="/" element={<Navigate to="/today" replace />} />
          <Route path="/today" element={<LibraryPage today />} />
          <Route path="/library" element={<LibraryWorkspace />} />
          <Route path="/library/:id" element={<LibraryWorkspace />} />
          <Route path="/ask" element={<AskPage />} />
          <Route path="/reminders" element={<RemindersPage />} />
          <Route path="/map" element={<Suspense fallback={<PageLoading />}><KnowledgeMap /></Suspense>} />
          <Route path="/trash" element={<TrashPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/today" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function PageHeader({ eyebrow, title, description, action }: { eyebrow: string; title: string; description: string; action?: React.ReactNode }) {
  return <header className="page-header"><div><span className="eyebrow">{eyebrow}</span><h1>{title}</h1><p>{description}</p></div>{action}</header>;
}

function PageLoading() {
  return <div className="page-loading"><span className="spinner" />Открываю ваш архив…</div>;
}

function CardImage({ card, className }: { card: MemoryCard; className: string }) {
  const [failedAttachmentId, setFailedAttachmentId] = useState<string | null>(null);
  const attachment = card.attachments.find((item) => item.role === "cover" && item.mime_type.startsWith("image/"))
    ?? card.attachments.find((item) => item.mime_type.startsWith("image/"));
  if (!attachment || failedAttachmentId === attachment.id) return null;
  return <img className={className} src={api.fileUrl(attachment.id)} alt={card.title} loading="lazy" onError={() => setFailedAttachmentId(attachment.id)} />;
}

function CardTile({ card }: { card: MemoryCard }) {
  return (
    <Link className="memory-card" to={`/library/${card.id}`}>
      <CardImage card={card} className="card-cover" />
      <div className="card-top"><span className={`type-dot type-${card.type}`} /><span>{CARD_LABELS[card.type]}</span><time>{formatDate(card.created_at)}</time></div>
      <h3>{card.title}</h3>
      <p>{card.summary || card.original_text}</p>
      <div className="tags">{card.tags.slice(0, 4).map((tag) => <span key={tag}>#{tag}</span>)}</div>
      <div className="card-footer"><span>{card.source_kind === "telegram" ? "Telegram" : card.source_kind}</span><ChevronRight size={17} /></div>
    </Link>
  );
}

function LibraryPage({ today = false }: { today?: boolean }) {
  const [cards, setCards] = useState<MemoryCard[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [type, setType] = useState("");
  const [composer, setComposer] = useState(false);

  const load = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    try {
      const result = await api.listCards({ type: type || undefined, archived: "false", limit: "100" });
      const day = new Date().toISOString().slice(0, 10);
      setCards(today ? result.cards.filter((card) => card.created_at.startsWith(day)) : result.cards);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "UNKNOWN");
    } finally { if (!background) setLoading(false); }
  }, [today, type]);

  useEffect(() => {
    void load();
    const interval = window.setInterval(() => { void load(true); }, 10_000);
    return () => window.clearInterval(interval);
  }, [load]);
  const visible = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("ru-RU");
    return normalized ? cards.filter((card) => [card.title, card.summary, ...card.tags].join(" ").toLocaleLowerCase("ru-RU").includes(normalized)) : cards;
  }, [cards, query]);

  return <>
    <PageHeader eyebrow={today ? "Входящие" : "Ваша память"} title={today ? "Сегодня" : "Библиотека"} description={today ? "Свежие мысли и материалы, которые появились сегодня." : "Идеи, ссылки и находки — разложены по карточкам."} action={<button className="primary-button" onClick={() => setComposer(true)}><Plus size={18} />Новая карточка</button>} />
    <div className="toolbar">
      <label className="search-field"><Search size={18} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Найти в текущем списке" /></label>
      <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Тип карточки"><option value="">Все типы</option>{Object.entries(CARD_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select>
    </div>
    <ErrorNotice error={error} />
    {loading ? <PageLoading /> : visible.length ? <section className="card-grid" aria-label="Карточки памяти">{visible.map((card) => <CardTile key={card.id} card={card} />)}</section> : <EmptyState title={today ? "Сегодня пока тихо" : "Архив ждёт первую мысль"} text="Отправьте сообщение боту или создайте карточку здесь." action={() => setComposer(true)} />}
    {composer && <CardComposer onClose={() => setComposer(false)} onCreated={() => { setComposer(false); void load(); }} />}
  </>;
}

function EmptyState({ title, text, action }: { title: string; text: string; action?: () => void }) {
  return <div className="empty-state"><span><Lightbulb /></span><h2>{title}</h2><p>{text}</p>{action && <button className="secondary-button" onClick={action}>Создать карточку</button>}</div>;
}

function CardComposer({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [type, setType] = useState("note");
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(event: FormEvent) {
    event.preventDefault(); setSaving(true);
    try {
      await api.createCard({ type, title, originalText: text, summary: text.slice(0, 500), sourceKind: "web", tags: tags.split(",").map((x) => x.trim()).filter(Boolean), entities: [], metadata: {} });
      onCreated();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "UNKNOWN"); setSaving(false); }
  }
  return <div className="modal-backdrop" role="presentation" onMouseDown={(e) => { if (e.currentTarget === e.target) onClose(); }}><form className="modal" onSubmit={submit}><div className="modal-title"><div><span className="eyebrow">Новая запись</span><h2>Сохранить мысль</h2></div><button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть"><X /></button></div><label>Тип<select value={type} onChange={(e) => setType(e.target.value)}>{Object.entries(CARD_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label><label>Заголовок<input required maxLength={240} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="О чём эта карточка?" /></label><label>Содержание<textarea required maxLength={20000} value={text} onChange={(e) => setText(e.target.value)} rows={7} placeholder="Запишите всё, что важно…" /></label><label>Теги<input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="кино, идеи, март" /></label><ErrorNotice error={error} /><button className="primary-button" disabled={saving}>{saving ? "Сохраняю…" : "Сохранить"}</button></form></div>;
}

function CardDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState<MemoryCard | null>(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.getCard(id).then((x) => setCard(x.card)).catch((e: Error) => setError(e.message)); }, [id]);
  if (error) return <><PageHeader eyebrow="Карточка" title="Не удалось открыть" description="Проверьте, существует ли эта запись." /><ErrorNotice error={error} /></>;
  if (!card) return <PageLoading />;
  async function remove() { await api.deleteCard(card!.id); navigate("/library"); }
  return <article className="detail-page"><Link className="back-link" to="/library">← К библиотеке</Link><div className="detail-hero"><div><span className="type-pill">{CARD_LABELS[card.type]}</span><h1>{card.title}</h1><p>{card.summary}</p></div><div className="detail-actions"><button className="secondary-button" onClick={() => setEditing(true)}>Изменить</button><button className="danger-button" onClick={() => void remove()}>В корзину</button></div></div><CardImage card={card} className="detail-cover" /><dl className="meta-list"><div><dt>Создано</dt><dd>{formatDate(card.created_at)}</dd></div><div><dt>Источник</dt><dd>{card.source_kind}</dd></div>{card.event_start && <div><dt>Дата события</dt><dd>{formatDate(card.event_start)}</dd></div>}</dl><section className="reading-card"><h2>Исходная запись</h2><p className="preserve-lines">{card.original_text}</p>{card.source_url && <a href={card.source_url} target="_blank" rel="noreferrer">Открыть источник ↗</a>}</section><div className="tags large">{card.tags.map((tag) => <span key={tag}>#{tag}</span>)}</div>{editing && <EditCard card={card} onClose={() => setEditing(false)} onSaved={(next) => { setCard(next); setEditing(false); }} />}</article>;
}

function EditCard({ card, onClose, onSaved }: { card: MemoryCard; onClose: () => void; onSaved: (card: MemoryCard) => void }) {
  const [title, setTitle] = useState(card.title);
  const [summary, setSummary] = useState(card.summary);
  const [originalText, setOriginalText] = useState(card.original_text);
  const [tags, setTags] = useState(card.tags.join(", "));
  async function submit(event: FormEvent) { event.preventDefault(); const result = await api.updateCard(card.id, { title, summary, originalText, tags: tags.split(",").map((x) => x.trim()).filter(Boolean) }); onSaved(result.card); }
  return <div className="modal-backdrop"><form className="modal" onSubmit={submit}><div className="modal-title"><h2>Изменить карточку</h2><button type="button" className="icon-button" onClick={onClose}><X /></button></div><label>Заголовок<input value={title} onChange={(e) => setTitle(e.target.value)} required /></label><label>Кратко<textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} /></label><label>Исходный текст<textarea value={originalText} onChange={(e) => setOriginalText(e.target.value)} rows={8} /></label><label>Теги<input value={tags} onChange={(e) => setTags(e.target.value)} /></label><button className="primary-button">Сохранить изменения</button></form></div>;
}

function AskPage() {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [cards, setCards] = useState<MemoryCard[]>([]);
  const [sources, setSources] = useState<Array<{ title: string; url: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(event: FormEvent) { event.preventDefault(); setLoading(true); setError(null); try { const result = await api.chat(question); setAnswer(result.answer); setCards(result.memoryCards ?? []); setSources(result.webSources ?? []); } catch (cause) { setError(cause instanceof Error ? cause.message : "UNKNOWN"); } finally { setLoading(false); } }
  return <><PageHeader eyebrow="Разговор с памятью" title="Спросите что угодно" description="Idea-bot сначала ищет в вашем архиве и только при необходимости обращается к интернету." /><form className="ask-box" onSubmit={submit}><MessageCircleQuestion /><textarea value={question} onChange={(e) => setQuestion(e.target.value)} required rows={3} placeholder="Например: какой фильм я хотела посмотреть в марте?" /><button className="primary-button" disabled={loading}>{loading ? "Ищу…" : "Спросить"}</button></form><ErrorNotice error={error} />{answer && <section className="answer-card"><span className="eyebrow">Ответ</span><p className="preserve-lines">{answer}</p>{cards.length > 0 && <><h3>Из вашей памяти</h3><div className="answer-links">{cards.map((card) => <Link key={card.id} to={`/library/${card.id}`}>{card.title}<ChevronRight size={15} /></Link>)}</div></>}{sources.length > 0 && <><h3>Из интернета</h3><div className="answer-links">{sources.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noreferrer">{source.title}<ChevronRight size={15} /></a>)}</div></>}</section>}</>;
}

function RemindersPage() {
  const [items, setItems] = useState<Reminder[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.reminders().then((x) => setItems(x.reminders)).catch((e: Error) => setError(e.message)); }, []);
  return <><PageHeader eyebrow="Не забыть" title="Напоминания" description="Разовые и повторяющиеся напоминания приходят прямо в Telegram." /><ErrorNotice error={error} /><div className="stack-list">{items.map((item) => <article key={item.id} className="list-row"><span className="row-icon"><Clock3 /></span><div><h3>{item.message}</h3><p>{new Date(item.next_run_at).toLocaleString("ru-RU", { dateStyle: "long", timeStyle: "short" })} · {item.timezone}</p></div><span className="status-chip">{item.rrule ? "Повторяется" : "Один раз"}</span></article>)}{!items.length && !error && <EmptyState title="Напоминаний пока нет" text="Напишите боту: «Напомни завтра в 10 позвонить врачу»." />}</div></>;
}

function TrashPage() {
  const [cards, setCards] = useState<MemoryCard[]>([]);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => api.listCards({ deleted: "true", limit: "100" }).then((x) => setCards(x.cards)).catch((e: Error) => setError(e.message)), []);
  useEffect(() => { void load(); }, [load]);
  return <><PageHeader eyebrow="30 дней на возврат" title="Корзина" description="Удалённые карточки хранятся здесь 30 дней, затем исчезают вместе с вложениями." /><ErrorNotice error={error} /><div className="stack-list">{cards.map((card) => <article key={card.id} className="list-row"><span className={`type-dot type-${card.type}`} /><div><h3>{card.title}</h3><p>Удалено {card.deleted_at ? formatDate(card.deleted_at) : "недавно"}</p></div><button className="secondary-button" onClick={() => api.restoreCard(card.id).then(load)}>Восстановить</button></article>)}{!cards.length && !error && <EmptyState title="Корзина пуста" text="Здесь появятся карточки, которые вы решите удалить." />}</div></>;
}

function SettingsPage() {
  const [timezone, setTimezone] = useState("Europe/Moscow");
  const [data, setData] = useState<Awaited<ReturnType<typeof api.settings>> | null>(null);
  const [status, setStatus] = useState("");
  useEffect(() => { api.settings().then((result) => { setData(result); if (typeof result.settings.timezone === "string") setTimezone(result.settings.timezone); }).catch(() => setStatus("Не удалось загрузить настройки")); }, []);
  async function save() { await api.saveSetting("timezone", timezone); setStatus("Часовой пояс сохранён"); }
  async function exportArchive() {
    setStatus("Собираю архив…");
    const exported = await api.exportData();
    const cards = (exported.cards ?? []) as MemoryCard[];
    const attachments = (exported.attachments ?? []) as Array<{ id: string; filename: string }>;
    const markdown = cards.map((card) => `# ${card.title}\n\n${card.summary}\n\n${card.original_text}\n\n${card.tags.map((tag) => `#${tag}`).join(" ")}\n`).join("\n---\n\n");
    const files: Record<string, Uint8Array> = {
      "idea-bot.json": strToU8(JSON.stringify(exported, null, 2)),
      "idea-bot.md": strToU8(markdown),
    };
    for (const attachment of attachments) {
      const response = await fetch(api.fileUrl(attachment.id));
      if (!response.ok) continue;
      const safeName = attachment.filename.replace(/[\\/:*?"<>|]+/g, "-");
      files[`attachments/${attachment.id}-${safeName}`] = new Uint8Array(await response.arrayBuffer());
    }
    const zip = zipSync(files);
    const url = URL.createObjectURL(new Blob([zip], { type: "application/zip" }));
    const link = document.createElement("a"); link.href = url; link.download = `idea-bot-${new Date().toISOString().slice(0, 10)}.zip`; link.click(); URL.revokeObjectURL(url);
    setStatus("Экспорт готов");
  }
  const supadataText = !data
    ? "загружается"
    : !data.supadata.configured
      ? "не подключена"
      : `${data.supadata.usage} из ${data.supadata.limit} кредитов (${data.supadata.percent}%)`;
  const supadataWarning = data?.supadata.configured && data.supadata.percent >= 80;
  return <><PageHeader eyebrow="Личные предпочтения" title="Настройки" description="Управление временем, моделями и резервной копией архива." /><div className="settings-grid"><section className="settings-card"><h2>Время</h2><p>Этот часовой пояс используется для всех напоминаний.</p><label>Часовой пояс<input value={timezone} onChange={(e) => setTimezone(e.target.value)} /></label><button className="secondary-button" onClick={() => void save()}>Сохранить</button></section><section className="settings-card"><h2>Интеграции</h2><p>Основная модель: <strong>{data?.models.primary ?? "будет настроена"}</strong></p><p>Vision-модель: <strong>{data?.models.vision ?? "будет настроена"}</strong></p><p>Tavily: <strong>{data?.tavily ? `${data.tavily.percent}% лимита` : "данные появятся после подключения"}</strong></p><p>Supadata: <strong>{supadataText}</strong></p>{supadataWarning && <p className="error-text">Использовано больше 80% месячного пакета Supadata.</p>}</section><section className="settings-card"><h2>Резервная копия</h2><p>JSON и Markdown собираются в браузере — без лишней нагрузки на Worker.</p><button className="secondary-button" onClick={() => void exportArchive()}><FileDown size={17} />Скачать ZIP</button></section></div>{status && <div className="notice">{status}</div>}</>;
}

export function App() { return <Shell />; }
